/**
 * PublicCountdownBanner — public-site countdown strip rendered below the
 * Home hero (public-site-countdown-banner spec, task 5.1; Open Question
 * #1 RESOLVED Option B).
 *
 * Unlike the portal `CountdownBanner` (dark `bg-gold-950` slab, peach
 * `#E2B091` numerals), this surface is themed with the Meridian/Reignara
 * public-site semantic tokens (`bg-card`, `text-foreground`,
 * `text-primary`, `font-display`) so it reads as part of the marketing
 * site rather than a transplant from the signed-in dashboard
 * (Requirements 6.1, 6.4).
 *
 * Data + math are shared with the portal banner so the two surfaces never
 * disagree (Requirements 4.4, 6.3):
 *   - Anchor sourced from `useCountdownAnchor(operativeYear)` where the
 *     operative year is resolved from the Season SSOT via
 *     `useActiveSeason()` (season-ssot Task 8.1); the same hook the
 *     portal uses, calling `getCountdownAnchor` with
 *     `authMode: 'apiKey'` so an unauthenticated visitor can load the
 *     countdown without a Cognito session (Requirements 1.1, 1.2, 2.1, 2.7).
 *   - Target derivation + D/H/M/S breakdown come from `@/lib/countdown`
 *     (`deriveTargetMs`, `computeRemaining`, `formatAnchorDate`).
 *
 * Two states, mirroring the portal's two-state semantics:
 *   1. Populated — anchor resolved AND target > now. Shows the
 *      Days / Hours / Minutes / Seconds row plus a subtitle derived from
 *      `anchor.date` (Requirements 1.3, 1.5, 6.2).
 *   2. No-anchor — no anchor, first response still in flight, target in
 *      the past, or an unparseable anchor. Shows the "Next pageant date
 *      coming soon" copy with NO digits and NO subtitle (Requirements 1.7,
 *      1.8, 2.3, 2.6, 3.1, 3.2, 3.3, 4.5).
 *
 * Error resilience (Requirements 2.4, 2.5, 2.8) is handled by the shared
 * `resolveState` cache-fallback resolver plus a `useRef` last-good target:
 *   - A later `error`/refetch with a previously resolved target keeps the
 *     Populated_State alive (computes against the cached target).
 *   - An error before any anchor has ever resolved falls through to the
 *     No_Anchor_State.
 *   - The hook surfaces a >10s pending request as an `error`, which lands
 *     in the same fallback paths above.
 *
 * Accessibility (Requirements 8.1–8.5):
 *   - Populated region: `role="timer"` + accessible name "Countdown to the
 *     next pageant", with NO `aria-live` so the per-second ticks are not
 *     announced to screen-reader users every second (8.5).
 *   - No-anchor region: `role="status"` + accessible name "No upcoming
 *     pageant date set".
 *   - Decorative icons carry `aria-hidden="true"`.
 *
 * Requirements: 1.1, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 2.2, 2.3, 2.4, 2.5,
 *   2.6, 3.1, 3.2, 3.3, 3.5, 4.5, 6.1, 6.2, 6.4, 7.1, 7.2, 7.3, 7.4,
 *   8.1, 8.2, 8.3, 8.4, 8.5
 */
import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { useCountdownAnchor } from '@/hooks/useCountdownAnchor';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import {
  computeRemaining,
  formatAnchorDate,
  resolveState,
} from '@/lib/countdown';
import { cn } from '@/lib/utils';

export interface PublicCountdownBannerProps {
  /** Optional className passthrough for the outer wrapper. */
  className?: string;
}

/**
 * No-anchor placeholder copy. Kept byte-for-byte identical to the portal
 * `CountdownBanner`'s `NoAnchorBanner` copy so the two surfaces stay
 * message-consistent (Requirement 3.4).
 */
const NO_ANCHOR_COPY = 'Next pageant date coming soon';

export function PublicCountdownBanner({
  className,
}: PublicCountdownBannerProps) {
  // season-ssot Task 8.1: resolve the competition year from the Season
  // SSOT (`useActiveSeason`) rather than the `COMPETITION_YEAR` build
  // constant (R4.1, R9.1); scope the anchor query to it (R9.2). An
  // `unresolved` resolver forces the No_Anchor_State so the public
  // surface never counts down to a bootstrap/calendar-derived anchor
  // (R9.3).
  const { operativeYear, status: seasonStatus } = useActiveSeason();
  const { anchor, loading, error } = useCountdownAnchor(operativeYear);
  const [now, setNow] = useState<number>(() => Date.now());

  // Tick once per second so the Populated_State recomputes its D/H/M/S
  // values without a page reload (Requirement 1.4). The interval is
  // released on unmount so no computation runs afterward (Requirement 1.6).
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Last-good target cache for the shared cache-fallback resolver. Holding
  // it in a ref keeps a previously resolved target alive across a later
  // `error`/refetch (Requirement 2.4) while letting an error-before-first-
  // resolve fall through to the No_Anchor_State (Requirement 2.5).
  const lastGoodTargetRef = useRef<number | null>(null);

  const resolved = resolveState({ loading, error, anchor }, now, lastGoodTargetRef.current);
  // Write the resolver's active target back as the new cached value.
  lastGoodTargetRef.current = resolved.targetMs;

  // season-ssot R9.3: when the active Season is unresolved, render the
  // No_Anchor_State regardless of any anchor fetched against the
  // bootstrap year — never count down to a bootstrap/calendar anchor.
  const seasonUnresolved = seasonStatus === 'unresolved';

  const outer = cn(
    'w-full max-w-full overflow-x-hidden bg-card text-foreground py-16',
    className,
  );
  const inner = 'max-w-7xl mx-auto px-6 lg:px-12 w-full max-w-full';

  if (seasonUnresolved || resolved.state === 'no-anchor') {
    return (
      <section
        data-testid="public-countdown-no-anchor"
        role="status"
        aria-label="No upcoming pageant date set"
        className={outer}
      >
        <div className={inner}>
          <div className="text-center">
            <Clock
              className="w-7 h-7 text-primary mx-auto mb-6"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <span className="font-sans text-xs uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
              Countdown to the Next Pageant
            </span>
            <p
              data-testid="public-countdown-no-anchor-copy"
              className="font-display text-3xl md:text-4xl text-foreground"
            >
              {NO_ANCHOR_COPY}
            </p>
            <div className="w-24 h-[1px] bg-primary mx-auto mt-6" />
          </div>
        </div>
      </section>
    );
  }

  // Populated_State. `resolved.targetMs` is guaranteed non-null and in the
  // future here, so `computeRemaining` is safe to call (Requirement 1.3).
  const { days, hours, minutes, seconds } = computeRemaining(
    resolved.targetMs as number,
    now,
  );

  // Subtitle derived from the anchor's `date` (never hardcoded,
  // Requirement 1.5). When a cached target is keeping the Populated_State
  // alive after the anchor went null (Requirement 2.4), `anchor` may be
  // null — guard so the banner still renders without a subtitle.
  const subtitle = anchor?.date ? formatAnchorDate(anchor.date) : '';

  return (
    <section
      data-testid="public-countdown-populated"
      role="timer"
      aria-label="Countdown to the next pageant"
      className={outer}
    >
      <div className={inner}>
        <div className="text-center mb-10">
          <Clock
            className="w-7 h-7 text-primary mx-auto mb-6"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <span className="font-sans text-xs uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
            Countdown to the Next Pageant
          </span>

          {/* Date subtitle, placed above the numerals. Typography matches
              the "Current Titleholders" section heading
              (`text-4xl md:text-5xl font-display text-foreground`) so the
              two headlines read consistently down the page. */}
          {subtitle && (
            <p
              data-testid="public-countdown-date"
              className="text-4xl md:text-5xl font-display text-foreground text-center mt-2"
            >
              {subtitle}
            </p>
          )}
        </div>

        {/*
          Responsive unit grid: a 2x2 stack at 320–767px (Requirement 7.1)
          that collapses to a single horizontal row at >= 1024px
          (Requirement 7.2). No fixed pixel widths, and the wrappers are
          `w-full max-w-full` so nothing forces horizontal page scrolling
          across 320–1920px (Requirements 7.3, 7.4).
        */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10 w-full max-w-full">
          <CountdownUnit value={days} label="Days" testId="public-countdown-days" />
          <CountdownUnit value={hours} label="Hours" testId="public-countdown-hours" />
          <CountdownUnit value={minutes} label="Minutes" testId="public-countdown-minutes" />
          <CountdownUnit value={seconds} label="Seconds" testId="public-countdown-seconds" />
        </div>
      </div>
    </section>
  );
}

interface CountdownUnitProps {
  value: number;
  label: string;
  testId: string;
}

/**
 * Single Days / Hours / Minutes / Seconds column. The four columns share
 * this component so they stay structurally identical and each remains
 * fully visible (no clipping/overlap) at every viewport width
 * (Requirement 7.4). Wrapper is `w-full max-w-full` with no fixed pixel
 * width so it flexes inside the responsive grid.
 */
function CountdownUnit({ value, label, testId }: CountdownUnitProps) {
  return (
    <div className="flex flex-col items-center w-full max-w-full">
      <span
        data-testid={testId}
        className="font-display text-5xl md:text-6xl italic text-primary leading-none tabular-nums"
      >
        {String(value).padStart(2, '0')}
      </span>
      <span className="font-sans text-[10px] md:text-xs uppercase tracking-[0.3em] text-muted-foreground mt-3">
        {label}
      </span>
    </div>
  );
}

export default PublicCountdownBanner;
